import React, { useEffect, useMemo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from '@reach/router'
import { Waypoint } from 'react-waypoint'

import ClassPicturePlaceholder from '_assets/images/class-placeholder-photo.jpg'
import CourseSectionLight from '_components/course-section-light'
import ArrowLeft from '_assets/icons/arrow-left.svg'
import IconButton, { ICON_BUTTON_SIZE } from '_components/icon-button'
import CourseTag, { COURSE_TAG_THEME } from '_components/course-tag'
import CourseIntention, { COURSE_INTENTION_THEME } from '_components/course-intention'
import ClassHubCoaches from '_components/class-hub-coaches'
import ClassAttachments, { CLASS_ATTACHMENTS_THEME } from '_components/class-attachments'
import ClassParticipants from '_components/class-participants'
import ClassChannel from '_components/class-channel'
import HubFooter from '_components/hub-footer'
import {
  getAllParticipants,
  startAssignmentVideo,
  getClassLight,
  getClassLightAssignments,
  getClassLightAttachments,
} from '_modules/class/actions'
import {
  getClassById,
  isStartingCallSelector,
  participantsSelector,
  isGetClassLightLoadingSelector,
  getClassLightSections,
  isGetClassLightSectionsLoadingSelector,
  getClassLightAttachmentsSelector,
} from '_modules/class/selectors'
import RocketLoader from '_components/rocket-loader'
import useWindowSize from '_hooks/use-window-size'
import { FLAG_MULTIPLE_COACHES } from '_config/environment'
import { CLASS_TYPE } from '_constants/class'

import styles from './styles.css'

const ClassHubDetails = () => {
  const dispatch = useDispatch()
  const isStartingCallLoading = useSelector(isStartingCallSelector)
  const { classId } = useParams()
  const isLoadingClass = useSelector(isGetClassLightLoadingSelector)
  const isLoadingSections = useSelector(isGetClassLightSectionsLoadingSelector)
  const currentClass = useSelector(state => getClassById(state, classId.toString()))
  const participants = useSelector(participantsSelector)
  const { isMobile } = useWindowSize()
  const sections = useSelector(getClassLightSections)
  const attachmentFiles = useSelector(getClassLightAttachmentsSelector)

  const onStartMeetingClick = useCallback(
    ({ meetingId, assignmentId }) => {
      dispatch(
        startAssignmentVideo({
          classId,
          meetingId,
          assignmentId,
        })
      )
    },
    [classId, dispatch]
  )

  const handleClassCoaches = useMemo(() => {
    if (FLAG_MULTIPLE_COACHES) {
      return currentClass?.instructors
    }

    if (currentClass?.instructor) {
      return [currentClass.instructor]
    }

    return []
  }, [currentClass])

  const isInstructor = useMemo(() => currentClass.isMainInstructor, [currentClass])

  useEffect(() => {
    if (classId) {
      dispatch(getAllParticipants(classId))

      dispatch(getClassLight(classId))
      dispatch(getClassLightAssignments(classId))
      dispatch(getClassLightAttachments(classId))
    }
  }, [classId, dispatch])

  const classCoverPhoto = useMemo(
    () => currentClass?.coverPhoto || ClassPicturePlaceholder,
    [currentClass]
  )

  const onLoadMore = useCallback(() => {
    dispatch(getClassLightAssignments(classId, sections.next))
  }, [classId, dispatch, sections.next])

  return (
    <section className={styles['class-hub-details-container']}>
      {isLoadingClass ? (
        <div className={styles['loader-wrapper']}>
          <RocketLoader />
        </div>
      ) : (
        <>
          <img
            className={styles['class-hub-details-cover']}
            src={classCoverPhoto}
            alt="Class background top cover"
          />
          <div className={styles['image-overlay']} />
          <div className={styles['content-wrapper']}>
            <div className={styles['class-hub-header']}>
              <CourseTag
                text={currentClass.category}
                theme={COURSE_TAG_THEME.DARK}
                className={styles['course-tag']}
              />
              <IconButton
                className={styles['back-icon-button']}
                size={ICON_BUTTON_SIZE.X_LARGE}
                href="/courses"
                icon={ArrowLeft}
              />
              <h1 className={styles['course-title']}>{currentClass?.name}</h1>
            </div>
            <div className={styles['class-content']}>
              {currentClass?.description && <p>{currentClass.description}</p>}
              {sections.results.map(section => (
                <CourseSectionLight
                  key={section.order}
                  isInstructor={isInstructor}
                  onStartMeeting={onStartMeetingClick}
                  isLeaderLed={currentClass?.type === CLASS_TYPE.COACH_LED.slug}
                  className={styles['course-section']}
                  isStartingCall={isStartingCallLoading}
                  assignmentClassName={styles.assignment}
                  assignmentsWrapperClassName={styles['assignments-wrapper']}
                  title={section.sectionName}
                  assignments={section.assignments}
                />
              ))}
              {sections.next && <Waypoint onEnter={onLoadMore} />}
              {isLoadingSections && (
                <div className={styles['page-loader-wrapper']}>
                  <RocketLoader />
                </div>
              )}
            </div>
            <div className={styles['class-details']}>
              <CourseIntention
                className={styles['course-intention-card']}
                intention={currentClass?.intention}
                theme={COURSE_INTENTION_THEME.CLASS_HUB}
                classId={currentClass?.id}
              />
              {handleClassCoaches?.length > 0 && (
                <ClassHubCoaches
                  classId={currentClass?.id}
                  className={styles['class-coaches']}
                  coaches={handleClassCoaches}
                />
              )}
              {!!attachmentFiles?.length && (
                <ClassAttachments
                  attachmentList={attachmentFiles}
                  theme={CLASS_ATTACHMENTS_THEME.CARD_ON_MOBILE}
                />
              )}
              {!!participants?.size && (
                <ClassParticipants classId={currentClass?.id} participantsList={participants} />
              )}
              {currentClass.channel && <ClassChannel channel={currentClass?.channel} />}
              {isMobile && <div className={styles.divider} />}
            </div>
          </div>
          {!isMobile && <HubFooter />}
        </>
      )}
    </section>
  )
}

export default ClassHubDetails
